import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { CheckCircleIcon, ViewIcon, CalendarIcon } from '@chakra-ui/icons';
import { apiGet } from '../lib/api';
import {
  getNavDefs,
  resolveNavPath,
  type SidebarNode,
  type SidebarNodeDef,
} from '../components/layout/navigationDefinitions';

export type Role = 'ADMIN' | 'TEACHER' | 'STUDENT' | 'UNKNOWN';

export const SIDEBAR_WIDTH = 380;
export const COLLAPSED_WIDTH = 80;

/** Minimal shapes the sidebar needs from the classroom/assignment endpoints. */
interface SidebarClassroom {
  code: string;
  name: string;
}

interface SidebarAssignment {
  code: string;
  title: string;
  completed?: boolean;
  createdAt?: string;
}

// Data + derivation for the sidebar navigation tree. View concerns live in
// SidebarContent / SidebarTreeItem so each file stays under 500 lines.
export function useSidebarNavigation(role: Role) {
  const location = useLocation();
  const { t, i18n } = useTranslation();

  const currentClassroomCode = location.pathname.match(/\/(my-)?classrooms\/([^/]+)/)?.[2];
  const currentAssignmentCode = location.pathname.match(/\/assignments\/([^/]+)/)?.[1];

  const { data: currentAssignment } = useQuery({
    queryKey:
      role === 'TEACHER'
        ? ['teacher-assignment', currentAssignmentCode]
        : ['student-assignment-detail', currentAssignmentCode],
    queryFn: () => {
      if (role === 'TEACHER') {
        return apiGet(`/api/teacher/assignments/${currentAssignmentCode}`);
      }
      return apiGet(`/api/student/assignments/${currentAssignmentCode}`);
    },
    enabled:
      (role === 'STUDENT' || role === 'TEACHER') &&
      !!currentAssignmentCode &&
      !currentClassroomCode,
    staleTime: 60000,
  });

  const { data: classroomsData } = useQuery<SidebarClassroom[]>({
    queryKey: ['my-classrooms', role],
    queryFn: () => apiGet(`/api/student/classrooms`),
    enabled: role === 'STUDENT' || role === 'TEACHER',
    staleTime: 60000,
  });

  const classrooms = classroomsData || [];
  const actualClassroomCode = currentClassroomCode || (currentAssignment as { classroomCode?: string } | null)?.classroomCode;

  const { data: assignmentsData } = useQuery({
    queryKey: ['classroom-assignments-sidebar', actualClassroomCode, role],
    queryFn: async () => {
      if (!actualClassroomCode) return null;
      if (role === 'TEACHER') {
        return await apiGet(`/api/teacher/classrooms/${actualClassroomCode}/assignments`);
      }
      const params = new URLSearchParams({
        page: '1',
        pageSize: '1000',
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });
      return await apiGet(
        `/api/student/classrooms/${actualClassroomCode}/assignments?${params.toString()}`,
      );
    },
    enabled: (role === 'STUDENT' || role === 'TEACHER') && !!actualClassroomCode,
    staleTime: 30000,
  });

  const assignments: SidebarAssignment[] =
    role === 'TEACHER'
      ? ((assignmentsData as SidebarAssignment[]) || [])
      : ((assignmentsData as { items?: SidebarAssignment[] } | null)?.items || []);

  const navDefs = useMemo(() => getNavDefs(role), [role]);

  const learningNav: SidebarNode[] = useMemo(() => {
    return navDefs.map((n: SidebarNodeDef) => {
      if (
        ((n.id === 'my-classrooms' && role === 'STUDENT') ||
          (n.id === 'classrooms' && role === 'TEACHER')) &&
        (classrooms as SidebarClassroom[]).length > 0
      ) {
        const children = (classrooms as SidebarClassroom[]).map((classroom) => {
          const classroomAssignments = classroom.code === actualClassroomCode ? assignments : [];

          const sortedAssignments = [...classroomAssignments].sort((a, b) => {
            if (role === 'STUDENT') {
              if (a.code === currentAssignmentCode) return -1;
              if (b.code === currentAssignmentCode) return 1;
              if (!a.completed && b.completed) return -1;
              if (a.completed && !b.completed) return 1;
              const dateA = a.createdAt ? new Date(a.createdAt).getTime() : 0;
              const dateB = b.createdAt ? new Date(b.createdAt).getTime() : 0;
              return dateB - dateA;
            }
            return 0;
          });

          return {
            id: `classroom-${classroom.code}`,
            label: classroom.name,
            icon: CalendarIcon,
            to: role === 'TEACHER' ? `/classrooms/${classroom.code}` : `/my-classrooms/${classroom.code}`,
            children: sortedAssignments.map((assignment: SidebarAssignment) => ({
              id: `assignment-${assignment.code}`,
              label: assignment.title,
              icon: assignment.completed ? CheckCircleIcon : ViewIcon,
              to:
                role === 'TEACHER'
                  ? `/assignments/${assignment.code}`
                  : `/student/assignments/${assignment.code}`,
              isCompleted: assignment.completed,
              isCurrent: assignment.code === currentAssignmentCode,
            })),
          };
        });

        return {
          id: n.id,
          label: t(n.labelKey) as string,
          icon: n.icon,
          to: resolveNavPath(n.id),
          children,
        };
      }

      return {
        id: n.id,
        label: t(n.labelKey) as string,
        icon: n.icon,
        to: n.children ? undefined : resolveNavPath(n.id),
        children: n.children?.map((c) => ({
          id: c.id,
          label: t(c.labelKey) as string,
          icon: c.icon,
          to: resolveNavPath(c.id),
          children: c.children?.map((gc) => ({
            id: gc.id,
            label: t(gc.labelKey) as string,
            icon: gc.icon,
            to: resolveNavPath(gc.id),
          })),
        })),
      };
    });
  }, [
    t,
    navDefs,
    i18n.language,
    role,
    classrooms,
    actualClassroomCode,
    currentAssignmentCode,
    assignments,
  ]);

  const [openMap, setOpenMap] = useState<Record<string, boolean>>(() => {
    try {
      return JSON.parse(localStorage.getItem('sidebar.open') || '{}') as Record<string, boolean>;
    } catch {
      return {};
    }
  });

  const toggle = useCallback(
    (id: string) => {
      setOpenMap((s) => ({ ...s, [id]: !s[id] }));
    },
    [setOpenMap],
  );

  useEffect(() => {
    try {
      localStorage.setItem('sidebar.open', JSON.stringify(openMap));
    } catch {
      // Storage unavailable: sidebar open-state simply is not persisted.
    }
  }, [openMap]);

  useEffect(() => {
    if (
      (currentAssignmentCode || currentClassroomCode) &&
      (role === 'STUDENT' || role === 'TEACHER')
    ) {
      const menuId = role === 'STUDENT' ? 'my-classrooms' : 'classrooms';
      setOpenMap((prev) => ({ ...prev, [menuId]: true }));
      if (actualClassroomCode) {
        setOpenMap((prev) => ({ ...prev, [`classroom-${actualClassroomCode}`]: true }));
      }
    }
  }, [currentAssignmentCode, currentClassroomCode, actualClassroomCode, role]);

  const normalizedQuery = '';

  function allowNode(nodeId: string): boolean {
    if (role === 'ADMIN') return true;

    if (role === 'TEACHER') {
      if (nodeId.startsWith('classrooms')) return true;
      if (nodeId.startsWith('classroom-')) return true;
      if (nodeId.startsWith('assignment-')) return true;
      if (nodeId === 'exercise-library') return true;
      if (nodeId === 'public-resources') return true;
      if (nodeId === 'indexed-documents') return true;
      if (nodeId.startsWith('settings')) return true;
      if (nodeId.startsWith('admin')) return false;
      if (nodeId.startsWith('students')) return false;
      return false;
    }

    if (role === 'STUDENT') {
      if (nodeId === 'my-classrooms') return true;
      if (nodeId === 'public-resources') return true;
      if (nodeId === 'indexed-documents') return false;
      if (nodeId.startsWith('classroom-')) return true;
      if (nodeId.startsWith('assignment-')) return true;
      if (nodeId.startsWith('settings')) return true;
      return false;
    }

    if (nodeId.startsWith('settings')) return true;
    return false;
  }

  const byRoleNav: SidebarNode[] = useMemo(
    () =>
      learningNav
        .map((n) => ({
          ...n,
          children: n.children
            ?.map((c) => ({
              ...c,
              children: c.children?.filter((gc) => allowNode(gc.id)),
            }))
            .filter((c) => allowNode(c.id) || (c.children && c.children.length > 0)),
        }))
        .filter((n) => allowNode(n.id) || (n.children && n.children.length > 0)),
    [learningNav, role],
  );

  const filteredNav: SidebarNode[] = useMemo(() => byRoleNav, [byRoleNav]);

  return { t, filteredNav, openMap, toggle, normalizedQuery };
}
